import { createRoot } from "react-dom/client";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";
import App from "./App.tsx";
import "./index.css";
import NoNetwork from "./pages/ValidationPages/NoNetwork.tsx";
import { PersistGate } from "redux-persist/integration/react";
import { store, persistor } from "./store/store";
import { Provider } from "react-redux";
import { ThemeProvider } from "@/components/common/ThemeProvider";
import { msalConfig } from "@/config/azureEntraIdConfig";
import { recordRedirectError } from "@/utils/silentSso";

/**
 * Initialize MSAL instance
 * This must be done before rendering the app
 */
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * MSAL's silent sign-in (ssoSilent / token renewal) loads our redirect URI in a hidden
 * iframe and reads the auth response from that iframe's URL. Don't boot the app there:
 * it is wasted work and could consume the response before the parent page reads it.
 */
const isMsalHiddenFrame =
  window.self !== window.top && /[#&?](code|error)=/.test(window.location.href);

if (!isMsalHiddenFrame) {
  // Initialize MSAL instance
  await msalInstance.initialize();

// Handle redirect promise
await msalInstance
  .handleRedirectPromise()
  .then((response) => {
    // The account that just signed in is the current identity
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
    }
  })
  .catch((error) => {
    // Lets the silent SSO flow decide its next step (e.g. account picker, MFA)
    recordRedirectError(error);
    console.error("Error handling redirect promise:", error);
  });

createRoot(document.getElementById("root")!).render(
  <NoNetwork>
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <MsalProvider instance={msalInstance}>
          <ThemeProvider>
            <App />
          </ThemeProvider>
        </MsalProvider>
      </PersistGate>
    </Provider>
  </NoNetwork>
);
}
